/**
 * @packageDocumentation
 * Contains the bet area widget that lets players adjust stake values, toggle fast play, and display current wager information.
 */
import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import { BottomBar } from "./bottomBar";
import { betSizes, currencySymbol } from "./server";
import { applyCent, brightnessFilter, disabledfilter, font_main } from "./global";
import { SoundManager } from "./sounds";

interface ContainerWithBg extends PIXI.Container {
  bgImg?: PIXI.AnimatedSprite;
}

export class betArea extends PIXI.Container {
  public betval: PIXI.Text = new PIXI.Text({ text: "" });

  public betTextContainer!: ContainerWithBg;
  public plusContainer!: ContainerWithBg;
  public minusContainer!: ContainerWithBg;

  static currentBetIndex: number = 0;
  public gameStarted: boolean = false;

  constructor() {
    super();

    this.CreatePlusText();
    this.CreateMinusText();
    this.CreateBetTextContainer();

    // ✅ Critical: lock the BET AREA bounds to the BET BOX ART size,
    // so app.ts "fitToSize" scales to Figma using the box, not the moving buttons or text metrics.
    this.freezeFitBoundsToBetBox();
  }

  CreateBetTextContainer() {
    this.betTextContainer = new PIXI.Container();

    const frames: PIXI.Texture[] = [Assets.get("Bet_but"), Assets.get("Bet_but")];
    const BetBg = new PIXI.AnimatedSprite(frames);

    BetBg.gotoAndStop(0);
    BetBg.anchor.set(0.5);
    BetBg.scale.set(0.8);
    BetBg.y = -5;

    this.betTextContainer.bgImg = BetBg;

    this.betval.text = currencySymbol + betSizes[betArea.currentBetIndex].toFixed(2);
    applyCent(this.betval, betSizes[betArea.currentBetIndex]);

    this.betval.style = {
      fontFamily: "PermanentMarker",
      align: "center",
      fill: "white",
      fontSize: 30,
      fontWeight: "bold",
      stroke: { color: "black", width: 4, join: "round" },
      letterSpacing: 1,
    };

    this.betval.anchor.set(0.5);
    this.betval.y = 2;

    this.betTextContainer.addChild(BetBg, this.betval);

    // Keep text inside the box
    this.fitBetTextToBox();

    // Position the bet amount box within the bet area container
    this.betTextContainer.x = 0;
    this.betTextContainer.y = -10;

    // ✅ Freeze betTextContainer bounds to the box art size (not text)
    this.freezeBoundsToDisplayObject(this.betTextContainer, BetBg);

    BottomBar.updateTotalBet(betSizes[betArea.currentBetIndex]);

    this.addChild(this.betTextContainer);
    this.betTextContainer.interactive = true;
  }

  CreatePlusText() {
    this.plusContainer = new PIXI.Container();

    const plusBg = new PIXI.Sprite(Assets.get("Bet_plus_but"));
    plusBg.anchor.set(0.5);
    plusBg.scale.set(0.75);
    plusBg.x = 10;
    plusBg.y = -10;

    const plusText = new PIXI.Text();
    plusText.text = "";
    plusText.style = {
      fontFamily: "PermanentMarker",
      dropShadow: { alpha: 0.8, angle: 1, blur: 4, color: "0x111111", distance: 2 },
      align: "center",
      fill: "white",
      fontSize: 40,
      fontWeight: "lighter",
    };
    plusText.anchor.set(0.5);

    this.plusContainer.addChild(plusBg, plusText);

    // Your authored placement
    this.plusContainer.x = 110;

    this.plusContainer.interactive = true;
    this.plusContainer.cursor = "pointer";
    this.plusContainer.on("pointertap", this.plusClickFun.bind(this));
    this.plusContainer.on("pointerup", () => (this.plusContainer.filters = [brightnessFilter]));
    this.plusContainer.on("pointerdown", () => (this.plusContainer.filters = [disabledfilter]));
    this.plusContainer.on("pointerover", () => (this.plusContainer.filters = [brightnessFilter]));
    this.plusContainer.on("pointerout", () => (this.plusContainer.filters = []));

    this.addChild(this.plusContainer);
  }

  CreateMinusText() {
    this.minusContainer = new PIXI.Container();

    const minusBg = new PIXI.Sprite(Assets.get("Bet_minus_but"));
    minusBg.anchor.set(0.5);
    minusBg.scale.set(0.78);
    minusBg.x = -10;
    minusBg.y = -10;

    const minusText = new PIXI.Text();
    minusText.text = "";
    minusText.style = {
      fontFamily: font_main,
      dropShadow: { alpha: 0.8, angle: 1, blur: 4, color: "0x111111", distance: 2 },
      align: "center",
      fill: "white",
      fontSize: 40,
      fontWeight: "lighter",
    };
    minusText.anchor.set(0.5);

    this.minusContainer.addChild(minusBg, minusText);

    // Your authored placement
    this.minusContainer.x = -150;

    this.minusContainer.interactive = true;
    this.minusContainer.cursor = "pointer";
    this.minusContainer.on("pointertap", this.minusClickFun.bind(this));
    this.minusContainer.on("pointerup", () => (this.minusContainer.filters = [brightnessFilter]));
    this.minusContainer.on("pointerdown", () => (this.minusContainer.filters = [disabledfilter]));
    this.minusContainer.on("pointerover", () => (this.minusContainer.filters = [brightnessFilter]));
    this.minusContainer.on("pointerout", () => (this.minusContainer.filters = []));

    this.minusContainer.filters = [disabledfilter];

    this.addChild(this.minusContainer);
  }

  plusClickFun() {
    this.minusContainer.interactive = true;
    this.minusContainer.filters = [];

    betArea.currentBetIndex++;

    if (betArea.currentBetIndex <= betSizes.length - 1) {
      SoundManager.coinDownSndPlay();
    }

    if (betArea.currentBetIndex >= betSizes.length - 1) {
      betArea.currentBetIndex = betSizes.length - 1;
      this.plusContainer.filters = [disabledfilter];
      this.plusContainer.interactive = false;
    }

    this.updateBetText();
  }

  minusClickFun() {
    this.plusContainer.interactive = true;
    this.plusContainer.filters = [];

    betArea.currentBetIndex--;

    if (betArea.currentBetIndex >= 0) {
      SoundManager.coinUpSndPlay();
    }

    if (betArea.currentBetIndex <= 0) {
      betArea.currentBetIndex = 0;
      this.minusContainer.filters = [disabledfilter];
      this.minusContainer.interactive = false;
    }

    this.updateBetText();
  }

  updateBetText() {
    this.betval.text = currencySymbol + betSizes[betArea.currentBetIndex].toFixed(2);
    applyCent(this.betval, betSizes[betArea.currentBetIndex]);

    BottomBar.updateTotalBet(betSizes[betArea.currentBetIndex]);

    // This may change text metrics, but bounds are frozen, so no resize shrink
    this.fitBetTextToBox();
  }

  private fitBetTextToBox() {
    const bg = this.betTextContainer?.bgImg;
    if (!bg) return;

    this.betval.scale.set(1);

    const maxW = bg.width - 30;
    if (this.betval.width > maxW) {
      this.betval.scale.set(maxW / this.betval.width);
    }
  }

  /**
   * Freeze container bounds to a specific display object's visual size (centered).
   * This prevents text/buttons from changing bounds used by app.ts fitting.
   */
  private freezeBoundsToDisplayObject(container: PIXI.Container, display: PIXI.DisplayObject) {
    const w = display.width;
    const h = display.height;
    container.boundsArea = new PIXI.Rectangle(-w / 2, -h / 2, w, h);
  }

  /**
   * The most important part:
   * Fit-to-Figma should scale this widget based on the Bet Box art, not moving buttons.
   */
  private freezeFitBoundsToBetBox() {
    const bg = this.betTextContainer?.bgImg;
    if (!bg) return;

    const w = bg.width;
    const h = bg.height;

    // Lock overall widget bounds to the box art size
    this.boundsArea = new PIXI.Rectangle(-w / 2, -h / 2, w, h);
  }
}
